import React, { useEffect, useState } from "react";
import ConfirmDialog from "../../ConfirmDialog";
import { TrainingLog } from "../../../types/post";
import { getLinkedSessionIDs } from "../../../utils/sessionLinks";

interface DeleteTrainingLogDialogProps {
  open: boolean;
  /** The log being deleted; drives whether the linked-data question shows. */
  log: Pick<
    TrainingLog,
    | "linkedSessionID"
    | "linkedSessionIDs"
    | "linkedActivityLogID"
    | "performanceType"
  > | null;
  /**
   * Performance training logs only: number of result logs whose
   * `linkedTrainingLogID` points at this log (PerformanceLog.md §4 "Delete").
   */
  linkedResultCount?: number;
  isDeleting?: boolean;
  onCancel: () => void;
  /**
   * `deleteLinkedSessionsAndActivity` is the athlete's answer to "Delete the
   * linked sessions and activity data too?" (plan decision 5). It is `false`
   * when the log has nothing linked. `deleteLinkedResults` is the answer to
   * "Also delete N linked results?" (performance logs only).
   */
  onConfirm: (
    deleteLinkedSessionsAndActivity: boolean,
    deleteLinkedResults?: boolean,
  ) => void;
}

/**
 * Confirmation for deleting a training log. When the log has linked sessions
 * or a wearable activity it asks the decision-5 question with three answers:
 * Cancel, "No, keep them" (unlink, sessions reappear under "Sessions to log")
 * and "Yes, delete them". A performance training log with linked results
 * first asks "Also delete N linked results?".
 */
const DeleteTrainingLogDialog: React.FC<DeleteTrainingLogDialogProps> = ({
  open,
  log,
  linkedResultCount = 0,
  isDeleting,
  onCancel,
  onConfirm,
}) => {
  const sessionCount = getLinkedSessionIDs(log).length;
  const hasActivity = Boolean(log?.linkedActivityLogID);
  const hasLinkedData = sessionCount > 0 || hasActivity;
  const isPerformance = Boolean(log?.performanceType);
  const asksResults = isPerformance && linkedResultCount > 0;

  // Stage 1: results question (performance logs). Stage 2: sessions question.
  const [resultsAnswer, setResultsAnswer] = useState<boolean | null>(null);
  useEffect(() => {
    if (!open) setResultsAnswer(null);
  }, [open]);

  const noun = isPerformance ? "performance" : "training log";

  if (asksResults && resultsAnswer === null) {
    const finishOrContinue = (deleteResults: boolean) => {
      if (hasLinkedData) {
        setResultsAnswer(deleteResults);
      } else {
        onConfirm(false, deleteResults);
      }
    };
    return (
      <ConfirmDialog
        open={open}
        title={`Delete ${noun}?`}
        message={`Also delete ${linkedResultCount} linked ${
          linkedResultCount === 1 ? "result" : "results"
        }? "No" keeps them as standalone performance results.`}
        loading={isDeleting}
        confirmLabel="Yes, delete results"
        secondaryLabel="No, keep them"
        destructive
        onConfirm={() => finishOrContinue(true)}
        onSecondary={() => finishOrContinue(false)}
        onCancel={onCancel}
      />
    );
  }

  const deleteResults = resultsAnswer === true;

  if (!hasLinkedData) {
    return (
      <ConfirmDialog
        open={open}
        title={`Delete ${noun}?`}
        message={`This ${noun} will be removed. This action cannot be undone.`}
        loading={isDeleting}
        confirmLabel="Delete"
        destructive
        onConfirm={() => onConfirm(false, deleteResults)}
        onCancel={onCancel}
      />
    );
  }

  const linkedSummary = [
    sessionCount > 0
      ? `${sessionCount} linked ${sessionCount === 1 ? "session" : "sessions"}`
      : null,
    hasActivity ? "wearable activity data" : null,
  ]
    .filter(Boolean)
    .join(" and ");

  return (
    <ConfirmDialog
      open={open}
      title={`Delete ${noun}`}
      message={`Delete the linked sessions and activity data too? This log has ${linkedSummary}. "No" keeps them: sessions return to "Sessions to log".`}
      loading={isDeleting}
      confirmLabel="Yes, delete them"
      secondaryLabel="No, keep them"
      destructive
      onConfirm={() => onConfirm(true, deleteResults)}
      onSecondary={() => onConfirm(false, deleteResults)}
      onCancel={onCancel}
    />
  );
};

export default DeleteTrainingLogDialog;
